import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent } from '../../../../ui';

/**
 * What the cascade delete of one call will touch, derived from the loaded rows of
 * the same document + user account. `deleted` follows the backend's feature filter;
 * `kept` are the other rows of the call, which stay.
 *
 * @ticket EB-4384
 */
export interface CallcenterDeleteDialogData {
  documentId: number;
  /** Feature labels of the loaded rows the cascade deletes. */
  deleted: string[];
  /** Feature labels of the call's other rows — not deleted, delete separately. */
  kept: string[];
  /**
   * Feature labels of loaded cascade rows that are already invoiced. Non-empty means
   * the backend refuses the whole delete (409, all-or-nothing), so the dialog blocks it.
   */
  invoiced: string[];
}

/**
 * Confirm for the per-call callcenter cascade delete. Spells out which rows go and
 * which stay, because a call document mixes callcenter charges with secretarial
 * rows that the cascade does not touch. Closes with `true` on confirm. The copy is
 * new (the KV deleted without a confirm) and was approved by Torsten on 2026-10-19.
 *
 * @ticket EB-4384
 */
@Component({
  selector: 'cmt-callcenter-delete-dialog',
  standalone: true,
  imports: [...HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideTrash2" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Delete Callcenter Charges</h2>
        </div>
      </hlm-dialog-header>

      <div hlmDialogBody class="space-y-3 text-base leading-relaxed text-foreground">
        <p data-testid="callcenter-delete-question">
          Delete the <strong>callcenter charges</strong> of call
          <span class="font-mono-feat">{{ data.documentId }}</span> for this user account?
        </p>

        <div>
          <p id="callcenter-delete-deleted-heading" class="font-semibold">Deleted</p>
          <ul
            class="list-disc pl-5"
            aria-labelledby="callcenter-delete-deleted-heading"
            data-testid="callcenter-delete-deleted-list"
          >
            @for (label of data.deleted; track $index) {
              <li>{{ label }}</li>
            }
          </ul>
        </div>

        @if (data.kept.length > 0) {
          <div>
            <p id="callcenter-delete-kept-heading" class="font-semibold">Stays — delete separately</p>
            <ul
              class="list-disc pl-5"
              aria-labelledby="callcenter-delete-kept-heading"
              data-testid="callcenter-delete-kept-list"
            >
              @for (label of data.kept; track $index) {
                <li>{{ label }}</li>
              }
            </ul>
          </div>
        }

        @if (blocked) {
          <div
            class="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2"
            role="alert"
            data-testid="callcenter-delete-invoiced"
          >
            <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-destructive" />
            <p class="text-sm text-foreground">
              Already invoiced: {{ data.invoiced.join(', ') }}. Nothing of this call can be deleted.
            </p>
          </div>
        } @else {
          <div
            class="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2"
            data-testid="callcenter-delete-note"
          >
            <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
            <p class="text-sm text-foreground">
              Matching charges of this call outside the loaded date range are deleted too.
              Nothing is deleted if one of them is already invoiced. This cannot be undone.
            </p>
          </div>
        }
      </div>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" data-testid="callcenter-delete-cancel" (click)="dialogRef.close(false)">
          {{ blocked ? 'Close' : 'Cancel' }}
        </button>
        @if (!blocked) {
          <button
            hlmBtn
            variant="destructive"
            data-testid="callcenter-delete-confirm"
            (click)="dialogRef.close(true)"
          >
            <hlm-icon name="lucideTrash2" size="1rem" />
            Delete callcenter charges
          </button>
        }
      </footer>
    </hlm-dialog-surface>
  `,
})
export class CallcenterDeleteDialogComponent {
  readonly dialogRef = inject(MatDialogRef<CallcenterDeleteDialogComponent, boolean>);
  readonly data: CallcenterDeleteDialogData = inject(MAT_DIALOG_DATA);

  /** True when an invoiced charge would make the backend refuse the delete. */
  protected get blocked(): boolean {
    return this.data.invoiced.length > 0;
  }
}
